import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const STATUS_COLORS = {
  Approved: 'var(--color-success)',
  Rejected: 'var(--color-error)',
  Denied: 'var(--color-error)',
  Pending: 'var(--color-warning)',
  'Under Review': 'var(--color-primary-light)',
};

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [fetchError, setFetchError] = useState('');

  const [grantProgram, setGrantProgram] = useState('');
  const [year, setYear] = useState('');
  const [projectType, setProjectType] = useState('');
  const [status, setStatus] = useState('Pending');
  const [legalName, setLegalName] = useState('');
  const [appId, setAppId] = useState('');
  const [appIdError, setAppIdError] = useState('');

  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/applications`, {
          headers: { Authorization: 'Bearer ' + token },
        });
        if (!res.ok) throw new Error('Failed to load applications');
        setApplications(await res.json());
      } catch {
        setFetchError('Could not load applications.');
      }
    };

    if (token) fetchApplications();
  }, [token]);

  useEffect(() => {
    const program = searchParams.get('program') || '';
    if (program) {
      setGrantProgram(program);
    }
  }, [searchParams]);

  const getYear = (application) => {
    const dateStr = application.submittedAt || application.createdAt;
    if (dateStr) {
      const date = new Date(dateStr);
      if (!Number.isNaN(date.getFullYear())) return date.getFullYear();
    }

    if (application.year) return Number(application.year);

    const match = (application.applicationNumber || application.id || '').match(/\d{4}/);
    return match ? parseInt(match[0], 10) : null;
  };

  const grantPrograms = [
    ...new Set(
      applications
        .map((application) => application.grantProgram || application.programCode)
        .filter(Boolean)
    ),
  ].sort();

  const years = [...new Set(applications.map(getYear).filter(Boolean))].sort((a, b) => b - a);

  const handleAppIdChange = (e) => {
    const val = e.target.value;
    setAppId(val);
    if (val && !/^[A-Za-z0-9]*$/.test(val)) {
      setAppIdError('Application ID must contain only letters and numbers.');
    } else {
      setAppIdError('');
    }
  };

  const handleReset = () => {
    setGrantProgram('');
    setYear('');
    setProjectType('');
    setStatus('Pending');
    setLegalName('');
    setAppId('');
    setAppIdError('');
  };

  const filtered = applications.filter((application) => {
    const applicantName = `${application.firstName || ''} ${application.lastName || ''}`.trim().toLowerCase();
    const alternateApplicantName = (application.legalName || '').toLowerCase();
    const projectName = `${application.projectName || application.title || ''}`.toLowerCase();
    const appYear = getYear(application);

    if (grantProgram) {
      if (application.grantProgram !== grantProgram && application.programCode !== grantProgram) {
        return false;
      }
    }

    if (year && appYear !== parseInt(year, 10)) return false;
    if (projectType && !projectName.includes(projectType.toLowerCase())) return false;
    if (status && application.status !== status) return false;
    if (
      legalName &&
      !applicantName.includes(legalName.toLowerCase()) &&
      !alternateApplicantName.includes(legalName.toLowerCase())
    ) {
      return false;
    }
    if (appId && !appIdError) {
      const searchTarget = `${application.applicationNumber || ''} ${String(application._id || application.id || '')}`.toLowerCase();
      if (!searchTarget.includes(appId.toLowerCase())) return false;
    }
    return true;
  });

  const groupedByYear = filtered.reduce((groups, application) => {
    const appYear = getYear(application);
    const key = appYear || 'Unknown';
    (groups[key] = groups[key] || []).push(application);
    return groups;
  }, {});

  const groupedYears = Object.keys(groupedByYear).sort((a, b) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return Number(b) - Number(a);
  });

  const tableHead = (
    <thead>
      <tr>
        <th>Application #</th>
        <th>Grant Name</th>
        <th>Applicant</th>
        <th>Program</th>
        <th>Amount</th>
        <th>Status</th>
      </tr>
    </thead>
  );

  const renderApplicationRow = (application) => {
    const applicationId = application._id || application.id || application.applicationNumber;
    const applicantDisplay =
      `${application.firstName || ''} ${application.lastName || ''}`.trim() ||
      application.legalName ||
      '-';

    return (
      <tr
        key={applicationId}
        style={{ cursor: applicationId ? 'pointer' : 'default' }}
        onClick={() => applicationId && navigate(`/dashboard/applications/${applicationId}`)}
      >
        <td>{application.applicationNumber || application.id || '-'}</td>
        <td>{application.projectName || application.title || '-'}</td>
        <td>{applicantDisplay}</td>
        <td>{application.grantProgram || application.programCode || '-'}</td>
        <td>
          {typeof application.fundingAmount === 'number'
            ? `$${application.fundingAmount.toLocaleString()}`
            : application.fundingAmount || '-'}
        </td>
        <td>
          <span
            className="status-badge"
            style={{ backgroundColor: STATUS_COLORS[application.status] || 'var(--text-secondary)' }}
          >
            {application.status}
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="applications-page page-stack">
      <section className="dashboard-section compact-toolbar">
        <div className="section-top compact-toolbar-top">
          <h2>Applications</h2>
          <button className="reset-filters-btn" onClick={handleReset}>
            Reset
          </button>
        </div>

        <div className="applications-filters applications-filters-compact">
          {grantPrograms.length > 0 && (
            <div className="filter-group">
              <label className="filter-label">Program</label>
              <select
                className="search-input filter-select"
                value={grantProgram}
                onChange={(e) => setGrantProgram(e.target.value)}
              >
                <option value="">All Programs</option>
                {grantPrograms.map((program) => (
                  <option key={program} value={program}>{program}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label className="filter-label">Year</label>
            <select
              className="search-input filter-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <option value="">All Years</option>
              {years.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Grant</label>
            <input
              type="text"
              className="search-input"
              placeholder="Search by grant name..."
              value={projectType}
              onChange={(e) => setProjectType(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="search-input filter-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Denied">Denied</option>
              <option value="Under Review">Under Review</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Applicant</label>
            <input
              type="text"
              className="search-input"
              placeholder="Search by first or last name..."
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">ID</label>
            <input
              type="text"
              className={`search-input${appIdError ? ' input-error' : ''}`}
              placeholder="Search by Application ID..."
              value={appId}
              onChange={handleAppIdChange}
            />
            {appIdError && <p className="field-error" style={{ marginTop: '4px' }}>{appIdError}</p>}
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-top">
          <h2>
            Results <span className="results-count">({filtered.length})</span>
          </h2>
        </div>

        {fetchError && <p className="section-error">{fetchError}</p>}

        {!fetchError && filtered.length === 0 && (
          <div className="grants-table-wrapper">
            <table className="grants-table">
              {tableHead}
              <tbody>
                <tr>
                  <td colSpan="6" className="no-results">
                    No applications match your filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {!fetchError && filtered.length > 0 && (
          <div className="applications-year-groups">
            {groupedYears.map((yearKey) => {
              const isActiveYear = year !== '' && String(year) === String(yearKey);
              const yearLabel = yearKey === 'Unknown' ? 'Year Unspecified' : yearKey;

              return (
                <div
                  key={yearKey}
                  className={`applications-year-group${isActiveYear ? ' is-active' : ''}`}
                >
                  <div className="applications-year-header">
                    <span className="applications-year-label">{yearLabel}</span>
                    <span className="results-count">({groupedByYear[yearKey].length})</span>
                  </div>
                  <div className="grants-table-wrapper">
                    <table className="grants-table">
                      {tableHead}
                      <tbody>{groupedByYear[yearKey].map(renderApplicationRow)}</tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default ApplicationsPage;
